import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Text,
  IconBanknote,
  IconColumns,
  IconCalendar,
  Badge,
  ActiveFilter,
  MagicTableFilterConfig,
  magicTableToApiQueryParams,
  useLinkMagicTableWithSearchParams,
  useMagicTableInfiniteQuery,
  useMagicTableSelectAll,
  IconCalendarDays,
  IconReceiptBill
} from '@sequencehq/core-components'

import { formatISODate } from '@sequencehq/utils'

import { InvoicesFilters } from '../types'
import {
  dueAfterFilterOptions,
  dueBeforeFilterOptions,
  invoiceAfterFilterOptions,
  invoiceBeforeFilterOptions,
  paymentStatusFilterOptions,
  sentFilterOptions,
  statusFilterOptions
} from './filters'
import { WithPagination } from '@sequencehq/api/dashboard/v20240509'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import { useEnabledCurrencies } from 'SettingsV2/index'
import {
  toInvoicePaymentStatusBadgeProps,
  toInvoiceStatusBadgeProps
} from '@sequencehq/utils'
import { customerIdFilterConfig } from 'Customer/customerMagicTableFilter'

type Invoice = Dashboardv99990101Api.GetInvoicesListSummary.InvoiceListSummary
type InvoiceStatus = Dashboardv99990101Api.Invoices.InvoiceStatus
type InvoicePaymentStatus = Dashboardv99990101Api.Invoices.InvoicePaymentStatus

type UseInvoicesMagicTable = ({
  forcedActiveFilters,
  onFiltersApiQueryParamsChanged
}: {
  forcedActiveFilters?: ActiveFilter[]
  onFiltersApiQueryParamsChanged: (newParams: string) => void
}) => {
  isLoading: boolean
  filters: MagicTableFilterConfig<InvoicesFilters>[]
  activeFilters: ActiveFilter[]
  sortBy: string | null
  onChangeActiveFilters: (newActiveFilters: ActiveFilter[]) => void
  onChangeSortBy: (sortBy: string | null) => void
  selectedInvoices: Array<string>
  setSelectedInvoices: (selectedInvoices: Array<string>) => void
  infiniteQuery: {
    data?:
      | {
          pages: WithPagination<{ items: ReadonlyArray<Invoice> }>[]
        }
      | null
      | undefined
    isFetching: boolean
    hasNextPage: boolean
    isFetchingNextPage: boolean
    fetchNextPage: () => void
  }
  selectAll: {
    showClearSelection: boolean
    showPrompt: boolean
    loading: boolean
    total: number
    totalSelected: number
    onSelectAll: () => void
    onClearSelection: () => void
  }
}

export const useInvoicesMagicTable: UseInvoicesMagicTable = ({
  forcedActiveFilters = [],
  onFiltersApiQueryParamsChanged
}) => {
  const [searchParams] = useSearchParams()
  const enabledCurrencyRes = useEnabledCurrencies()

  // The billing schedule filter only exists for deep links from a billing
  // schedule page. The active value rides on the include param or, for an
  // "is not" filter, the exclude param — the config must exist for either,
  // or the filter can't be parsed back out of the URL / saved view.
  const billingScheduleIdSearchParam =
    searchParams.get('billingScheduleId') ??
    searchParams.get('excludeBillingScheduleId')
  const billingScheduleIdOptions = billingScheduleIdSearchParam
    ? [
        {
          value: billingScheduleIdSearchParam,
          label: billingScheduleIdSearchParam
        }
      ]
    : null

  const currencyFilterOptions = useMemo(() => {
    if (enabledCurrencyRes.status === 'SUCCESS') {
      return enabledCurrencyRes.enabledCurrencies.map(currency => ({
        value: currency,
        label: currency
      }))
    }
    return []
  }, [enabledCurrencyRes])

  const isLoading = enabledCurrencyRes.status !== 'SUCCESS'

  const { data: cachedInvoicesData } = useQuery(
    dashboardv99990101Client.getInvoices,
    {
      search: '',
      limit: 20
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      select: data =>
        data?.items?.filter((invoice: any) => invoice.invoiceNumber) ?? []
    }
  )

  const filtersConfig = [
    ...(billingScheduleIdOptions
      ? [
          {
            type: 'multiSelect',
            paramName: 'billingScheduleId',
            excludeParamName: 'excludeBillingScheduleId',
            options: billingScheduleIdOptions ?? [],
            format: (value: string) => <Text>{value}</Text>,
            label: 'Billing schedule',
            icon: IconCalendarDays
          }
        ]
      : []),
    {
      ...customerIdFilterConfig,
      paramName: 'customerId',
      excludeParamName: 'excludeCustomerId',
      label: 'Customer'
    },
    {
      type: 'multiSelectAsync',
      paramName: 'invoiceNumber',
      excludeParamName: 'excludeInvoiceNumber',
      label: 'Invoice number',
      icon: IconReceiptBill,
      fetcher: async (term: string) => {
        if (!term && cachedInvoicesData) {
          return cachedInvoicesData
        }

        const queryResult = await dashboardv99990101Client.getInvoices({
          search: term,
          limit: 20
        })

        return (
          queryResult.data?.items?.filter(invoice => invoice.invoiceNumber) ??
          []
        )
      },
      mapToOption: (invoice: { id: string; invoiceNumber: string }) => ({
        value: invoice.invoiceNumber,
        label: invoice.invoiceNumber
      })
    },
    {
      type: 'multiSelect',
      paramName: 'invoiceStatus',
      excludeParamName: 'excludeInvoiceStatus',
      options: statusFilterOptions(),
      format: status => (
        <Badge
          {...toInvoiceStatusBadgeProps({
            status: status as InvoiceStatus
          })}
        />
      ),
      label: 'Status',
      icon: IconColumns,
      optionsSortFn: () => 0
    },
    {
      type: 'multiSelect',
      paramName: 'invoicePaymentStatus',
      excludeParamName: 'excludeInvoicePaymentStatus',
      options: paymentStatusFilterOptions(),
      format: status => (
        <Badge
          {...toInvoicePaymentStatusBadgeProps({
            status: status as InvoicePaymentStatus
          })}
        />
      ),
      label: 'Payment status',
      icon: IconColumns,
      optionsSortFn: () => 0
    },
    {
      type: 'multiSelect',
      paramName: 'invoiceCurrency',
      excludeParamName: 'excludeInvoiceCurrency',
      label: 'Currency',
      icon: IconBanknote,
      options: currencyFilterOptions,
      format: currency => currency
    },
    {
      type: 'date',
      paramName: 'due',
      paramNameBefore: 'dueBefore',
      paramNameAfter: 'dueAfter',
      optionsBefore: dueBeforeFilterOptions,
      optionsAfter: dueAfterFilterOptions,
      format: (_, label) => label,
      label: 'Due',
      stringifyDate: date => formatISODate(date),
      icon: IconCalendar
    },
    {
      type: 'date',
      paramName: 'sent',
      paramNameBefore: 'sentBefore',
      paramNameAfter: 'sentAfter',
      optionsBefore: sentFilterOptions,
      optionsAfter: sentFilterOptions,
      format: (_, label) => label,
      label: 'Sent',
      stringifyDate: date => formatISODate(date),
      icon: IconCalendar
    },
    {
      type: 'date',
      paramName: 'invoice',
      paramNameBefore: 'invoiceBefore',
      paramNameAfter: 'invoiceAfter',
      optionsBefore: invoiceBeforeFilterOptions,
      optionsAfter: invoiceAfterFilterOptions,
      format: (_, label) => label,
      label: 'Invoice date',
      stringifyDate: date => formatISODate(date),
      icon: IconCalendar
    },
    {
      type: 'date',
      paramName: 'billingPeriod',
      paramNameBefore: 'billingPeriodBefore',
      paramNameAfter: 'billingPeriodAfter',
      optionsBefore: invoiceBeforeFilterOptions,
      optionsAfter: invoiceAfterFilterOptions,
      format: (_, label) => label,
      label: 'Billing period',
      stringifyDate: date => formatISODate(date),
      icon: IconCalendar,
      hideCustomDate: true
    },
    {
      type: 'toggle',
      paramName: 'excludeZeroQuantity',
      label: 'Hide zero-usage invoices'
    }
  ] as MagicTableFilterConfig<InvoicesFilters>[]

  const filters = filtersConfig.map(filter => ({
    ...filter,
    disabled: !!forcedActiveFilters.find(
      forcedFilter => forcedFilter.paramName === filter.paramName
    )
  }))

  const {
    activeFilters: searchActiveFilters,
    onChangeActiveFilters: onChangeSearchActiveFilters,
    sortBy,
    onChangeSortBy
  } = useLinkMagicTableWithSearchParams(filters)

  const activeFilters = useMemo(
    () => [...forcedActiveFilters, ...searchActiveFilters],
    [forcedActiveFilters, searchActiveFilters]
  )

  const onChangeActiveFilters = useCallback(
    (newActiveFilters: ActiveFilter[]) => {
      onChangeSearchActiveFilters(
        newActiveFilters.filter(
          filter =>
            !forcedActiveFilters.find(
              forcedFilter => forcedFilter.paramName === filter.paramName
            )
        )
      )
    },
    [onChangeSearchActiveFilters, forcedActiveFilters]
  )

  const [prevQueryParams, setPrevQueryParams] = useState<string>('')

  useEffect(() => {
    const nextQueryParams = new URLSearchParams(
      magicTableToApiQueryParams(filters, activeFilters, sortBy)
    ).toString()

    if (nextQueryParams === prevQueryParams) {
      return
    }

    setPrevQueryParams(nextQueryParams)
    onFiltersApiQueryParamsChanged(nextQueryParams)
  }, [
    filters,
    activeFilters,
    sortBy,
    onFiltersApiQueryParamsChanged,
    prevQueryParams
  ])

  const invoicesPerPage = 100
  const { infiniteQuery } = useMagicTableInfiniteQuery<
    WithPagination<{ items: Invoice[] }>
  >(
    dashboardv99990101Client.getInvoicesListSummary,
    filters,
    activeFilters,
    sortBy,
    invoicesPerPage
  )

  const { selectedIds, setSelectedIds, selectAll } = useMagicTableSelectAll({
    infiniteQuery,
    activeFilters,
    pageSize: invoicesPerPage
  })

  return {
    isLoading,
    selectedInvoices: selectedIds,
    setSelectedInvoices: setSelectedIds,
    filters,
    activeFilters,
    sortBy,
    onChangeActiveFilters,
    onChangeSortBy,
    infiniteQuery,
    selectAll
  }
}
